/**
 * Supabase-compatible auth storage. This is the one seam that differs per
 * platform — web = `localStorage`; native = `expo-secure-store` (Keychain),
 * injected in P0.6 — and keeping it an interface is what lets the shared client
 * stay framework-neutral (no `import.meta.env`, no `window` assumptions).
 */
export interface StorageAdapter {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

/**
 * Browser `localStorage` adapter. Degrades to an in-memory map when
 * `localStorage` is unavailable (SSR, privacy mode, tests) so constructing the
 * client never throws.
 */
export function createWebStorageAdapter(): StorageAdapter {
  try {
    const ls = globalThis.localStorage;
    if (ls) {
      return {
        getItem: (k) => ls.getItem(k),
        setItem: (k, v) => ls.setItem(k, v),
        removeItem: (k) => ls.removeItem(k),
      };
    }
  } catch {
    // localStorage access can throw (e.g. sandboxed iframe) — fall through.
  }
  const mem = new Map<string, string>();
  return {
    getItem: (k) => mem.get(k) ?? null,
    setItem: (k, v) => {
      mem.set(k, v);
    },
    removeItem: (k) => {
      mem.delete(k);
    },
  };
}
